import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, Link, useLocation } from 'react-router-dom';
import { ToastContainer } from './components/ui';
import './App.css';
import navStyles from './App.module.css';

import {
  HomeIcon,
  UserGroupIcon,
  UserIcon,
  CalendarIcon,
  AcademicCapIcon,
  BanknotesIcon,
  CreditCardIcon,
  DocumentTextIcon,
  ArrowsRightLeftIcon,
  HeartIcon,
  TrophyIcon,
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  PlusIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
  BookOpenIcon,
  ClipboardDocumentListIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  FolderOpenIcon,
  FlagIcon,
  StarIcon,
  BellAlertIcon,
  DocumentCheckIcon,
} from '@heroicons/react/24/outline';

import Login from './components/Login';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Dashboard from './components/Dashboard';
import Students from './components/Students';
import StudentForm from './components/StudentForm';
import Timetable from './components/Timetable';
import Classes from './components/Classes';
import SubjectAllocations from './components/SubjectAllocations';
import AcademicSetupWizard from './components/AcademicSetupWizard';
import BulkEnroll from './components/BulkEnroll';
import Attendance from './components/attendance/Attendance';
import ExamResults from './components/ExamResults';
import Fees from './components/Fees';
import Payments from './components/Payments';
import PaymentPlans from './components/PaymentPlans';
import ExchangeRates from './components/ExchangeRates';
import Clubs from './components/Clubs';
import Sports from './components/Sports';
import Communication from './components/Communication';
import Reports from './components/Reports';
import Teachers from './components/Teachers';
import TeacherForm from './components/TeacherForm';
import AuditLog from './components/AuditLog';
import StaffRoles from './components/StaffRoles';
import EventCalendar from './components/EventCalendar';
import Documents from './components/Documents';
import Houses from './components/houses/Houses';
import OutstandingFees from './components/OutstandingFees';
import ReportCards from './components/reports/ReportCards';
import ReportCardReview from './components/reports/ReportCardReview';
import StudentImport from './components/StudentImport';
import SpecialBoard from './components/houses/SpecialBoard';
import ParentDashboard from './components/parent/ParentDashboard';
import ChildDetail from './components/parent/ChildDetail';
import { hasAnyRole, userRoles } from './utils/roles';
import { decodeJwt } from './utils/jwt';
import { applyTheme, resetTheme } from './utils/theme';
import SuperAdminApp from './superadmin/SuperAdminApp';
import ImpersonationBanner from './superadmin/ImpersonationBanner';

const ALL_ROLES = ['administrator', 'principal', 'head_of_academics', 'teacher', 'parent', 'student', 'coach'];
// Parents get their own child-centred screens (My Children) in place of the
// staff-shaped Students / Attendance / Fees / Payments / Reports pages.
const ALL_BUT_PARENT = ALL_ROLES.filter((role) => role !== 'parent');
const ACADEMIC_STAFF = ['administrator', 'principal', 'head_of_academics'];

const NAV_GROUPS = [
  {
    label: 'Academics',
    items: [
      { label: 'My Children', icon: UserGroupIcon, path: '/my-children', roles: ['parent'] },
      { label: 'Students', icon: UserGroupIcon, path: '/students', roles: [...ACADEMIC_STAFF, 'teacher', 'student'] },
      { label: 'Teachers', icon: UserIcon, path: '/teachers', roles: [...ACADEMIC_STAFF, 'teacher'] },
      { label: 'Academic Setup', icon: CheckCircleIcon, path: '/academic-setup', roles: ['administrator', 'principal'] },
      { label: 'Classes', icon: UserGroupIcon, path: '/classes', roles: ACADEMIC_STAFF },
      { label: 'Bulk Enroll', icon: UserGroupIcon, path: '/bulk-enroll', roles: ACADEMIC_STAFF },
      { label: 'Subject Allocations', icon: AcademicCapIcon, path: '/subject-allocations', roles: ACADEMIC_STAFF },
      { label: 'Timetable', icon: CalendarIcon, path: '/timetable', roles: ALL_ROLES, feature: 'timetable' },
      { label: 'Attendance', icon: ClipboardDocumentListIcon, path: '/attendance', roles: ALL_BUT_PARENT, feature: 'attendance' },
      { label: 'Exams', icon: AcademicCapIcon, path: '/exams', roles: [...ACADEMIC_STAFF, 'teacher'], feature: 'exam_results' },
      { label: 'Report Cards', icon: DocumentCheckIcon, path: '/report-cards', roles: [...ACADEMIC_STAFF, 'teacher'], feature: 'reports' },
    ],
  },
  {
    label: 'Finance',
    items: [
      { label: 'Fees', icon: BanknotesIcon, path: '/fees', roles: ['administrator', 'principal'], feature: 'fees' },
      { label: 'Outstanding Fees', icon: BellAlertIcon, path: '/fees/outstanding', roles: ['administrator', 'principal'], feature: 'fees' },
      { label: 'Payments', icon: CreditCardIcon, path: '/payments', roles: ['administrator', 'principal'], feature: 'payments' },
      { label: 'Payment Plans', icon: DocumentTextIcon, path: '/payment-plans', roles: ['administrator', 'principal'], feature: 'payment_plans' },
      { label: 'Exchange Rates', icon: ArrowsRightLeftIcon, path: '/exchange-rates', roles: ['administrator', 'principal'], feature: 'exchange_rates' },
    ],
  },
  {
    label: 'Activities',
    items: [
      { label: 'Calendar', icon: CalendarDaysIcon, path: '/calendar', roles: ALL_ROLES, feature: 'event_calendar' },
      { label: 'Clubs', icon: HeartIcon, path: '/clubs', roles: ALL_BUT_PARENT, feature: 'clubs' },
      { label: 'Sports', icon: TrophyIcon, path: '/sports', roles: ALL_BUT_PARENT, feature: 'sports' },
      { label: 'Houses', icon: FlagIcon, path: '/houses', roles: ALL_ROLES, feature: 'houses' },
      { label: 'Special Board', icon: StarIcon, path: '/special-board', roles: [...ACADEMIC_STAFF, 'teacher'], feature: 'houses' },
      { label: 'Documents', icon: FolderOpenIcon, path: '/documents', roles: ALL_ROLES, feature: 'documents' },
    ],
  },
  {
    label: 'Admin',
    items: [
      { label: 'Communication', icon: ChatBubbleLeftRightIcon, path: '/communication', roles: [...ACADEMIC_STAFF, 'teacher'], feature: 'communication' },
      { label: 'Reports', icon: ChartBarIcon, path: '/reports', roles: ALL_BUT_PARENT, feature: 'reports' },
      { label: 'Activity Log', icon: ClipboardDocumentListIcon, path: '/audit-log', roles: ['administrator', 'principal'] },
      { label: 'User Accounts', icon: UserIcon, path: '/staff-roles', roles: ['administrator', 'principal'] },
    ],
  },
];

function visibleNavGroups(roles, enabledFeatures = {}) {
  return NAV_GROUPS
    .map(group => ({
      ...group,
      items: group.items.filter(item =>
        hasAnyRole({ all_roles: roles }, item.roles) && (!item.feature || enabledFeatures[item.feature] !== false)
      ),
    }))
    .filter(group => group.items.length > 0);
}

const ROUTE_ROLE_OVERRIDES = {
  '/students/new': ['administrator', 'principal', 'head_of_academics', 'teacher'],
  '/students/import': ['administrator', 'principal', 'head_of_academics'],
  '/teachers/new': ['administrator', 'principal'],
  '/staff-roles': ['administrator', 'principal'],
};

function routeRolesFor(path) {
  if (ROUTE_ROLE_OVERRIDES[path]) return ROUTE_ROLE_OVERRIDES[path];
  for (const group of NAV_GROUPS) {
    const item = group.items.find(i => i.path === path);
    if (item) return item.roles;
  }
  return ALL_ROLES;
}

function routeFeatureFor(path) {
  for (const group of NAV_GROUPS) {
    const item = group.items.find(i => i.path === path);
    if (item) return item.feature || null;
  }
  return null;
}

function RequireRole({ roles, features = {}, path, children }) {
  if (!hasAnyRole({ all_roles: roles }, routeRolesFor(path))) {
    return <Navigate to="/dashboard" replace />;
  }
  const feature = routeFeatureFor(path);
  if (feature && features[feature] === false) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function NavDropdown({ group, currentPath, isOpen, onToggle, onHoverOpen, onClose, onNavigate }) {
  const isActive = group.items.some(item => {
    if (item.path === '/dashboard') return currentPath === '/dashboard' || currentPath === '/';
    return currentPath.startsWith(item.path);
  });

  return (
    <div className={navStyles.navGroup}>
      <button
        className={`${navStyles.navTrigger} ${isActive ? navStyles.navTriggerActive : ''}`}
        onClick={onToggle}
        onMouseEnter={onHoverOpen}
      >
        {group.label}
        <ChevronDownIcon style={{ width: 14, height: 14 }} />
      </button>
      {isOpen && (
        <div className={navStyles.navDropdown}>
          {group.items.map(item => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={navStyles.dropdownItem}
                onClick={() => { onClose(); onNavigate?.(); }}
              >
                <Icon />
                {item.label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MobileMenu({ roles, features, currentPath, onNavigate, onClose }) {
  const groups = visibleNavGroups(roles, features);
  const canAddStudent = hasAnyRole({ all_roles: roles }, ['administrator', 'principal', 'head_of_academics', 'teacher']);
  const canAddTeacher = hasAnyRole({ all_roles: roles }, ['administrator', 'principal']);

  return (
    <div className={`${navStyles.mobileMenu} ${navStyles.open}`}>
      {groups.map(group => (
        <div key={group.label} className={navStyles.mobileGroup}>
          <div className={navStyles.mobileGroupLabel}>{group.label}</div>
          {group.items.map(item => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={navStyles.mobileItem}
                onClick={onNavigate}
              >
                <Icon />
                {item.label}
              </Link>
            );
          })}
        </div>
      ))}
      {(canAddStudent || canAddTeacher) && (
        <div className={navStyles.mobileGroup}>
          <div className={navStyles.mobileGroupLabel}>Quick Actions</div>
          {canAddStudent && (
            <Link to="/students/new" className={navStyles.mobileItem} onClick={onNavigate}>
              <PlusIcon />
              Add Student
            </Link>
          )}
          {canAddTeacher && (
            <Link to="/teachers/new" className={navStyles.mobileItem} onClick={onNavigate}>
              <PlusIcon />
              Add Teacher
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

function AppLayout({ token, user, onLogout }) {
  const location = useLocation();
  const currentPath = location.pathname;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const navDesktopRef = useRef(null);
  const roles = userRoles(user);
  const enabledFeatures = user?.features || {};
  const navGroups = visibleNavGroups(roles, enabledFeatures);
  const claims = decodeJwt(token);
  const isImpersonating = Boolean(claims?.impersonating);

  useEffect(() => {
    const handleClick = (e) => {
      if (navDesktopRef.current && !navDesktopRef.current.contains(e.target)) setOpenGroup(null);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => { setOpenGroup(null); }, [currentPath]);

  return (
    <div className="App">
      {isImpersonating && (
        <ImpersonationBanner token={token} actingAsName={user?.full_name} impersonatorUsername={claims?.impersonator_username} />
      )}
      <nav className={navStyles.navbar}>
        <Link to="/dashboard" className={navStyles.brand}>
          <div className={navStyles.brandIcon}>
            {user?.theme?.logo_data_uri ? (
              <img src={user.theme.logo_data_uri} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            ) : (
              <BookOpenIcon style={{ width: 20, height: 20 }} />
            )}
          </div>
          <div>
            <div className={navStyles.brandName}>{user?.school_name || 'Best School'}</div>
            <span className={navStyles.brandSub}>Management System</span>
          </div>
        </Link>

        <div className={navStyles.navDesktop} ref={navDesktopRef}>
          <Link
            to="/dashboard"
            className={`${navStyles.navTrigger} ${currentPath === '/dashboard' || currentPath === '/' ? navStyles.navTriggerActive : ''}`}
          >
            <HomeIcon style={{ width: 16, height: 16 }} />
            Dashboard
          </Link>
          {navGroups.map(group => (
            <NavDropdown
              key={group.label}
              group={group}
              currentPath={currentPath}
              isOpen={openGroup === group.label}
              onToggle={() => setOpenGroup(openGroup === group.label ? null : group.label)}
              onHoverOpen={() => { if (openGroup !== null) setOpenGroup(group.label); }}
              onClose={() => setOpenGroup(null)}
            />
          ))}
        </div>

        <div className={navStyles.navRight}>
          <div className={navStyles.userInfo}>
            <UserIcon style={{ width: 16, height: 16 }} />
            <span>{user?.full_name}</span>
            <span className={navStyles.userRole}>({roles.includes('head_of_academics') ? 'head of academics' : user?.role})</span>
          </div>
          <button className={navStyles.logoutBtn} onClick={onLogout}>
            <ArrowRightOnRectangleIcon style={{ width: 16, height: 16 }} />
            <span className={`no-print ${navStyles.logoutText}`}>Logout</span>
          </button>
          <button
            className={navStyles.mobileToggle}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <XMarkIcon style={{ width: 24, height: 24 }} /> : <Bars3Icon style={{ width: 24, height: 24 }} />}
          </button>
        </div>
      </nav>

      {mobileOpen && <MobileMenu roles={roles} features={enabledFeatures} currentPath={currentPath} onNavigate={() => setMobileOpen(false)} />}

      <div className="container">
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" />} />
          <Route path="/dashboard" element={<Dashboard token={token} user={user} />} />
          <Route path="/students" element={<RequireRole roles={roles} features={enabledFeatures} path="/students"><Students token={token} user={user} /></RequireRole>} />
          <Route path="/students/new" element={<RequireRole roles={roles} features={enabledFeatures} path="/students/new"><StudentForm token={token} user={user} /></RequireRole>} />
          <Route path="/students/edit/:id" element={<RequireRole roles={roles} features={enabledFeatures} path="/students/new"><StudentForm token={token} user={user} /></RequireRole>} />
          <Route path="/academic-setup" element={<RequireRole roles={roles} features={enabledFeatures} path="/academic-setup"><AcademicSetupWizard token={token} user={user} /></RequireRole>} />
          <Route path="/classes" element={<RequireRole roles={roles} features={enabledFeatures} path="/classes"><Classes token={token} user={user} /></RequireRole>} />
          <Route path="/bulk-enroll" element={<RequireRole roles={roles} features={enabledFeatures} path="/bulk-enroll"><BulkEnroll token={token} user={user} /></RequireRole>} />
          <Route path="/subject-allocations" element={<RequireRole roles={roles} features={enabledFeatures} path="/subject-allocations"><SubjectAllocations token={token} user={user} /></RequireRole>} />
          <Route path="/timetable" element={<RequireRole roles={roles} features={enabledFeatures} path="/timetable"><Timetable token={token} user={user} /></RequireRole>} />
          <Route path="/attendance" element={<RequireRole roles={roles} features={enabledFeatures} path="/attendance"><Attendance token={token} user={user} /></RequireRole>} />
          <Route path="/exams" element={<RequireRole roles={roles} features={enabledFeatures} path="/exams"><ExamResults token={token} /></RequireRole>} />
          <Route path="/students/import" element={<RequireRole roles={roles} features={enabledFeatures} path="/students/import"><StudentImport token={token} /></RequireRole>} />
          <Route path="/report-cards" element={<RequireRole roles={roles} features={enabledFeatures} path="/report-cards"><ReportCards token={token} user={user} /></RequireRole>} />
          <Route path="/report-cards/:id" element={<RequireRole roles={roles} features={enabledFeatures} path="/report-cards"><ReportCardReview token={token} user={user} /></RequireRole>} />
          <Route path="/fees/outstanding" element={<RequireRole roles={roles} features={enabledFeatures} path="/fees/outstanding"><OutstandingFees token={token} /></RequireRole>} />
          <Route path="/fees" element={<RequireRole roles={roles} features={enabledFeatures} path="/fees"><Fees token={token} user={user} /></RequireRole>} />
          <Route path="/payments" element={<RequireRole roles={roles} features={enabledFeatures} path="/payments"><Payments token={token} user={user} /></RequireRole>} />
          <Route path="/payment-plans" element={<RequireRole roles={roles} features={enabledFeatures} path="/payment-plans"><PaymentPlans token={token} /></RequireRole>} />
          <Route path="/exchange-rates" element={<RequireRole roles={roles} features={enabledFeatures} path="/exchange-rates"><ExchangeRates token={token} /></RequireRole>} />
          <Route path="/clubs" element={<RequireRole roles={roles} features={enabledFeatures} path="/clubs"><Clubs token={token} user={user} /></RequireRole>} />
          <Route path="/sports" element={<RequireRole roles={roles} features={enabledFeatures} path="/sports"><Sports token={token} user={user} /></RequireRole>} />
          <Route path="/communication" element={<RequireRole roles={roles} features={enabledFeatures} path="/communication"><Communication token={token} /></RequireRole>} />
          <Route path="/reports" element={<RequireRole roles={roles} features={enabledFeatures} path="/reports"><Reports token={token} /></RequireRole>} />
          <Route path="/teachers" element={<RequireRole roles={roles} features={enabledFeatures} path="/teachers"><Teachers token={token} user={user} /></RequireRole>} />
          <Route path="/teachers/new" element={<RequireRole roles={roles} features={enabledFeatures} path="/teachers/new"><TeacherForm token={token} /></RequireRole>} />
          <Route path="/teachers/edit/:id" element={<RequireRole roles={roles} features={enabledFeatures} path="/teachers/new"><TeacherForm token={token} /></RequireRole>} />
          <Route path="/audit-log" element={<RequireRole roles={roles} features={enabledFeatures} path="/audit-log"><AuditLog token={token} /></RequireRole>} />
          <Route path="/staff-roles" element={<RequireRole roles={roles} features={enabledFeatures} path="/staff-roles"><StaffRoles token={token} user={user} /></RequireRole>} />
          <Route path="/calendar" element={<RequireRole roles={roles} features={enabledFeatures} path="/calendar"><EventCalendar token={token} user={user} /></RequireRole>} />
          <Route path="/houses" element={<RequireRole roles={roles} features={enabledFeatures} path="/houses"><Houses token={token} user={user} /></RequireRole>} />
          <Route path="/special-board" element={<RequireRole roles={roles} features={enabledFeatures} path="/special-board"><SpecialBoard token={token} user={user} /></RequireRole>} />
          <Route path="/documents" element={<RequireRole roles={roles} features={enabledFeatures} path="/documents"><Documents token={token} user={user} /></RequireRole>} />
          <Route path="/my-children" element={<RequireRole roles={roles} features={enabledFeatures} path="/my-children"><ParentDashboard token={token} user={user} /></RequireRole>} />
          <Route path="/my-children/:id" element={<RequireRole roles={roles} features={enabledFeatures} path="/my-children"><ChildDetail token={token} user={user} /></RequireRole>} />
        </Routes>
      </div>
    </div>
  );
}

function MainApp() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [user, setUser] = useState(null);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  }, []);

  const fetchUserInfo = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setUser(await response.json());
      } else {
        handleLogout();
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  }, [token, handleLogout]);

  useEffect(() => {
    if (token && !user) {
      fetchUserInfo();
    }
  }, [token, user, fetchUserInfo]);

  useEffect(() => {
    if (user?.theme?.colors) {
      applyTheme(user.theme.colors);
    } else {
      resetTheme();
    }
    return () => resetTheme();
  }, [user]);


  const handleLogin = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  if (!token) {
    return <Login onLogin={handleLogin} />;
  }

  if (!user) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <>
      <AppLayout token={token} user={user} onLogout={handleLogout} />
      <ToastContainer toasts={[]} onClose={() => {}} />
    </>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/superadmin/*" element={<SuperAdminApp />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/*" element={<MainApp />} />
      </Routes>
    </Router>
  );
}

export default App;
